/**
 * 规则页 PART 3 里的两个彩票玩法（Milhar / Milhar Hora）。
 *
 * <p>单独成文件而不是塞进 {@link ./RulesPage RulesPage}：那页已经 400 行，而这一段
 * 要拉两个接口、算窗口、造演示号，混进去之后谁都不敢动。它只接收示例那一期的
 * `D₁..D₁₀`，别的自己拉。
 *
 * <p>为什么窗口图只画一次、不放进任何一张卡片：它讲的是**两个玩法之间的关系**
 * （各读一段、互不重叠），塞进单张卡片就只剩半句话。同理，比对演示只在 4 位档卡片
 * 里出现一次，整点档卡片一句话引回去 —— 规则一模一样，讲两遍是噪音。
 *
 * <p>三条口径与页面其余部分一致：示例用真实开过的那一期；赔付/窗口/比例一律来自
 * 接口（V237 之后运营能在后台调 RTP，写死会开始撒谎且不报错）；不新增语义色，
 * 金只表示「这是开出来的结果」。
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchMilharStatus, type MilharStatus } from '../../features/milhar/api';
import { gamingRoundSeq, programSlotSeconds } from '../../shared/game/roundLabel';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import type { MessageKey } from '../../shared/i18n/messages';
import { fmtCurrency } from '../../shared/lib/format';
import {
  demoBrokenPick, demoPick, inWindow, normalizeWindow, payoutLadder, suffixMatch, windowResult,
  type DigitWindow,
} from '../../shared/milhar/rulesWindow';
import type { MilharTierId } from '../../shared/milhar/tiers';

const D_SUBSCRIPTS = '₁₂₃₄₅₆₇₈₉';

/** D₁..D₁₀ 的下标写法。D₁₀ 没有单字符下标，单独处理。 */
function dLabel(position: number) {
  return position < 10 ? `D${D_SUBSCRIPTS[position - 1]}` : 'D₁₀';
}

function windowLabel(window: DigitWindow) {
  return `${dLabel(window[0])}–${dLabel(window[1])}`;
}

/**
 * 两档的图标。
 *
 * <p>没有走 `shared/game/GameIcon` —— 那个的 `GameCode` 只有房间三玩法，Milhar 不在
 * 里面。这里按 D4 的图标语言现画：1.6px 描边、纯几何、一档一个签名色，
 * 最后一格填实，因为那是「从这里开始比」的那一位。
 */
function TierIcon({ digits, tone }: { digits: number; tone: string }) {
  const gap = 1.2;
  const width = (21 - gap * (digits - 1)) / digits;
  return (
    <span className="mr-tier-icon" aria-hidden="true">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="100%" height="100%" fill="none">
        {Array.from({ length: digits }, (_, i) => (
          <rect
            key={i}
            x={1.5 + i * (width + gap)} y={6.5} width={width} height={11} rx={1.6}
            stroke={tone} strokeWidth={1.6}
            fill={i === digits - 1 ? tone : 'none'}
            opacity={i === digits - 1 ? 0.9 : 0.55}
          />
        ))}
      </svg>
    </span>
  );
}

type TierView = {
  id: MilharTierId;
  titleKey: MessageKey;
  cadenceKey: MessageKey;
  tone: 'four' | 'hora';
  window: DigitWindow;
  result: string;
  status: MilharStatus | undefined;
};

type Tr = (key: MessageKey, params?: Record<string, string | number>) => string;

export function MilharRulesSection({ digits, cutoffTs, hourlyAnchor }: {
  digits: number[]; cutoffTs: number; hourlyAnchor: boolean;
}) {
  const t = useI18nStore((state) => state.t);
  const tr: Tr = (key, params) => {
    let out = t(key);
    if (params) for (const [k, v] of Object.entries(params)) out = out.split(`%${k}%`).join(String(v));
    return out;
  };

  // 两档各拉一次 status：窗口、赔付倍数、票价、入池比例、MADURO 位数都在里面。
  // status 不要求登录，所以规则页不传 token —— 访客也该看得到规则。
  const four = useQuery({
    queryKey: ['milhar-rules-config', '4'],
    queryFn: () => fetchMilharStatus(null, '4'),
    staleTime: 300_000,
    retry: false,
  });
  const hora = useQuery({
    queryKey: ['milhar-rules-config', '5'],
    queryFn: () => fetchMilharStatus(null, '5'),
    staleTime: 300_000,
    retry: false,
  });

  const tiers: TierView[] = useMemo(() => ([
    { id: '4' as MilharTierId, titleKey: 'milharTitle' as MessageKey,
      cadenceKey: 'mrulesCadence4' as MessageKey, tone: 'four' as const, status: four.data },
    { id: '5' as MilharTierId, titleKey: 'milharTier5Title' as MessageKey,
      cadenceKey: 'mrulesCadence5' as MessageKey, tone: 'hora' as const, status: hora.data },
  ]).map((tier) => {
    const window = normalizeWindow(tier.status?.config?.digitWindow, tier.id);
    return { ...tier, window, result: windowResult(digits, window) };
  }), [four.data, hora.data, digits]);

  /** 两档都不读的位置。今天只有 D₁₀，但窗口是配置项，别写死成一个数。 */
  const idlePositions = useMemo(
    () => digits.map((_, i) => i + 1).filter((position) => !tiers.some((tier) => inWindow(position, tier.window))),
    [digits, tiers],
  );

  return (
    <>
      <div className="mr-lead">
        <div className="mr-lead-title">{t('mrulesLeadTitle')}</div>
        <p className="mr-lead-body">{t('mrulesLeadBody')}</p>
      </div>

      <div className="mr-windows">
        <div className="mr-drow">
          {digits.map((d, i) => {
            const owner = tiers.find((tier) => inWindow(i + 1, tier.window));
            return (
              <span key={i} className={`mr-wcell${owner ? ` mr-wcell-${owner.tone}` : ' mr-wcell-idle'}`}>{d}</span>
            );
          })}
        </div>
        <div className="mr-brackets">
          {tiers.map((tier) => (
            <span
              key={tier.id}
              className={`mr-bracket mr-bracket-${tier.tone}`}
              style={{ gridColumn: `${tier.window[0]} / span ${tier.window[1] - tier.window[0] + 1}` }}
            >
              <span className="mr-bracket-line" />
              <span className="mr-bracket-name">{t(tier.titleKey)}</span>
            </span>
          ))}
          {/* 没有任何一档读的位置。只画一道灰条，名字放到下面的注里 —— 一格宽
              （37px）放不下任何一句能读的话，硬塞只会截成「não...」。 */}
          {idlePositions.map((position) => (
            <span key={position} className="mr-bracket mr-bracket-idle" style={{ gridColumn: `${position} / span 1` }}>
              <span className="mr-bracket-line" />
            </span>
          ))}
        </div>
        {idlePositions.length ? (
          <div className="mr-idle-note">{tr('mrulesWindowUnused', { d: idlePositions.map(dLabel).join(' · ') })}</div>
        ) : null}
      </div>

      <div className="rules-explain-callout">
        <span className="rules-explain-icon">🔒</span>
        <div className="rules-diy-body-wrap">
          <p className="rules-explain-body">{t('mrulesWindowNoOverlap')}</p>
        </div>
      </div>

      {tiers.map((tier) => (
        <TierCard key={tier.id} tier={tier} cutoffTs={cutoffTs} hourlyAnchor={hourlyAnchor} t={t} tr={tr} />
      ))}

      <AccumulatedNote tier={tiers[0]} t={t} tr={tr} />
    </>
  );
}

/** 一档的卡片：读哪一段、这期读出来是多少、怎么对号、赔付阶梯。 */
function TierCard({ tier, cutoffTs, hourlyAnchor, t, tr }: {
  tier: TierView; cutoffTs: number; hourlyAnchor: boolean;
  t: (key: MessageKey) => string; tr: Tr;
}) {
  const config = tier.status?.config;
  const currency = tier.status?.pool?.currency || 'BRL';
  const ticketPrice = Number(config?.ticketPrice ?? NaN);
  const digits = tier.window[1] - tier.window[0] + 1;
  const ladder = payoutLadder(config?.multipliers, digits, ticketPrice);
  const hasLadder = ladder.some((row) => row.payout != null);
  const maduro = config?.maduroDigits;
  /**
   * 同一期在两张卡片上会显示两个不同的号（如 #169 与 #14）。这是 App 本来就有的
   * 口径：两个玩法各自编号，注单和结果列表都按各自的号对齐（shared/game/roundLabel.ts）。
   * 每张卡片只讲自己那个玩法，所以拿自己的号是对的。
   */
  const seq = gamingRoundSeq(cutoffTs, programSlotSeconds(tier.id === '5' ? 'milhar-hora' : 'milhar'));

  return (
    <div className={`rules-game-card mr-card mr-card-${tier.tone}`}>
      <div className="rules-game-header">
        <TierIcon digits={digits} tone={tier.tone === 'four' ? '#22d3ee' : '#c084fc'} />
        <span className="rules-game-title">{t(tier.titleKey)}</span>
        <span className={`mr-cadence mr-cadence-${tier.tone}`}>{t(tier.cadenceKey)}</span>
      </div>

      <p className="rules-game-rule">
        {tr('mrulesGameRule', { w: windowLabel(tier.window), d: digits })}
      </p>

      {/* 整点档只在示例期**真的是**一期整点档开奖时才敢展示结果 —— 拿别的期切一段
          D₅–D₉ 冒充，是一个 Milhar Hora 从没开出过的号，而页首还写着「可去
          Etherscan 核对」。宁可少一块，也不许猜（同赔付表取不到时的处理）。 */}
      {tier.result && (tier.id !== '5' || hourlyAnchor) ? (
        <div className="mr-result">
          <div className="mr-result-label">{tr('mrulesThisRoundResult', { r: seq ? `#${seq}` : '' })}</div>
          <div className="mr-result-boxes">
            {tier.result.split('').map((d, i) => (
              <span key={i} className="rules-result-box rules-result-box-lg">{d}</span>
            ))}
          </div>
        </div>
      ) : null}

      {/* 比对演示只在 4 位档出现一次；整点档规则一模一样，一句话引回去。 */}
      {tier.id === '4' && tier.result
        ? <CompareDemo tier={tier} t={t} tr={tr} />
        : <p className="mr-note">{t('mrulesSameCheck')}</p>}

      {tier.id === '5' ? <p className="mr-note">{t('mrulesHoraOnTheHour')}</p> : null}

      <div className="mr-ladder-title">{t('mrulesPayoutTitle')}</div>
      {hasLadder ? (
        <>
          <div className="mr-ladder">
            <div className="mr-ladder-head">
              <span>{t('mrulesPayoutHit')}</span>
              <span>{t('mrulesPayoutPays')}</span>
            </div>
            {ladder.map((row) => (
              <div key={row.matched} className={`mr-ladder-row${row.multiplier == null ? ' mr-ladder-row-jackpot' : ''}`}>
                <span className="mr-ladder-hit">
                  {row.multiplier == null
                    ? tr('mrulesPayoutJackpotRow', { n: digits })
                    : row.matched === 1 ? t('mrulesPayoutDigit1') : tr('mrulesPayoutDigits', { n: row.matched })}
                </span>
                <span className="mr-ladder-pay">
                  {row.multiplier == null ? (
                    <span className="mr-ladder-jackpot">{t('milharPoolLabel')}</span>
                  ) : (
                    <>
                      <b>{fmtCurrency(row.payout, currency)}</b>
                      <i className="mr-ladder-mult">{row.multiplier}×</i>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
          <div className="mr-ladder-foot">{tr('mrulesPayoutOn', { v: fmtCurrency(ticketPrice, currency) })}</div>
        </>
      ) : (
        /* 倍数是可调参数，取不到就整块不渲染 —— 规则页猜一个数字比空着糟得多。 */
        <div className="mr-ladder-empty">{t('mrulesConfigUnavailable')}</div>
      )}

      {maduro != null && maduro < digits ? (
        <p className="mr-maduro-line">{tr('mrulesMaduroLine', { m: maduro })}</p>
      ) : null}
    </div>
  );
}

/**
 * 对号演示：一个中 2 位的正例，一个「前面全对、末位错」的反例。
 *
 * <p>反例是这一段存在的主要理由。玩家默认按「对上几个数字」理解，于是一注 0 元的
 * 号会被当成中奖 —— 这也是开奖动画里那条红线（doc/design/MILHAR_DRAW_EXPERIENCE_SPEC.md §0）。
 */
function CompareDemo({ tier, t, tr }: { tier: TierView; t: (key: MessageKey) => string; tr: Tr }) {
  const result = tier.result;
  const config = tier.status?.config;
  const currency = tier.status?.pool?.currency || 'BRL';
  const ticketPrice = Number(config?.ticketPrice ?? NaN);
  const hitCount = Math.min(2, result.length - 1);
  const multiplier = Number(config?.multipliers?.[String(hitCount)] ?? NaN);
  const prize = Number.isFinite(multiplier) && Number.isFinite(ticketPrice)
    ? multiplier * ticketPrice : null;

  return (
    <>
      <div className="mr-demo mr-demo-win">
        <div className="mr-demo-rows">
          <DemoRow label={t('mrulesCompareYours')} value={demoPick(result, hitCount)} result={result} />
          <DemoRow label={t('mrulesCompareDraw')} value={result} result={result} isResult />
        </div>
        <div className="mr-demo-start">
          <span className="mr-demo-start-text">← {t('mrulesCompareCountFrom')}</span>
        </div>
        <div className="mr-demo-verdict mr-demo-verdict-win">
          <span className="mr-demo-hits">{tr('mrulesCompareHits', { n: hitCount })}</span>
          {prize != null ? (
            <span className="mr-demo-prize">{tr('mrulesComparePays', { v: fmtCurrency(prize, currency) })}</span>
          ) : null}
        </div>
      </div>

      <div className="mr-demo mr-demo-lose">
        <div className="mr-demo-rows">
          <DemoRow label={t('mrulesCompareYours')} value={demoBrokenPick(result)} result={result} />
          <DemoRow label={t('mrulesCompareDraw')} value={result} result={result} isResult />
        </div>
        <div className="mr-demo-verdict mr-demo-verdict-lose">
          <span className="mr-demo-hits">{t('mrulesCompareZero')}</span>
        </div>
        <div className="mr-break">
          <div className="mr-break-title">{t('mrulesBreakTitle')}</div>
          <p className="mr-break-body">{t('mrulesBreakBody')}</p>
        </div>
      </div>
    </>
  );
}

/**
 * 一行号码。命中的那几位（**只有从末位起连续的**）才点亮。
 *
 * <p>「相同但不连续」的位刻意画成灰的：那正是这一段要纠正的误解，给它别的颜色
 * 只会让玩家以为它有价值。
 */
function DemoRow({ label, value, result, isResult }: {
  label: string; value: string; result: string; isResult?: boolean;
}) {
  const matched = suffixMatch(value, result);
  return (
    <div className="mr-demo-row">
      <span className="mr-demo-label">{label}</span>
      <span className="mr-demo-digits">
        {value.split('').map((d, i) => {
          const isHit = i >= value.length - matched;
          const cls = isResult
            ? 'mr-demo-digit mr-demo-digit-result'
            : `mr-demo-digit${isHit ? ' mr-demo-digit-hit' : ''}`;
          return <span key={i} className={cls}>{d}</span>;
        })}
      </span>
    </div>
  );
}

/** 奖池是两档共有的概念，所以放在两张卡片之后，讲一次。比例全部来自接口。 */
function AccumulatedNote({ tier, t, tr }: { tier: TierView; t: (key: MessageKey) => string; tr: Tr }) {
  const config = tier.status?.config;
  const contrib = config?.jackpotContribPct;
  const seed = config?.seedRetainPct;
  const takeHome = seed != null && Number.isFinite(Number(seed)) ? String(100 - Number(seed)) : null;
  if (contrib == null || seed == null || takeHome == null) return null;

  return (
    <div className="mr-acc">
      <div className="mr-acc-title">{t('mrulesAccTitle')}</div>
      <p className="mr-acc-body">
        {tr('mrulesAccBody', { p: `${contrib}%`, t: `${takeHome}%`, s: `${seed}%` })}
      </p>
      <p className="mr-acc-foot">{t('mrulesAccShared')}</p>
    </div>
  );
}
